import transition from "../helpers/transition";
import { setClassName, setAttr } from "../helpers/dom";

import Entities from "./shared/Entities";
import { createState } from "./shared/BasicState";

export default class Toggle {
  constructor() {
    this.entities = new Entities("Toggle", "[data-toggle]", Toggle.initSingle, Toggle.destroySingle);
  }

  static initSingle(containerEl) {
    const id = containerEl.getAttribute("data-toggle");
    const stateId = containerEl.getAttribute("data-toggle-state") ?? id;
    const key = containerEl.getAttribute("data-toggle-key") ?? true;
    const minOne = containerEl.getAttribute("data-toggle-min-one") !== null;

    const activeClass = containerEl.getAttribute("data-toggle-class");
    const activeAttr = containerEl.getAttribute("data-toggle-attr") ?? "data-active";
    const transitionAttr = containerEl.getAttribute("data-toggle-transition-attr") ?? "data-transition";

    const mode = containerEl.getAttribute("data-toggle-mode") ?? null;

    const toggleObj = {
      el: containerEl,
      stateId,
      key,
      activeClass,
      activeAttr,
      transitionAttr,
      mode,
    };

    let lastKey = containerEl.hasAttribute(activeAttr) ? key : null;

    const state = createState(stateId, lastKey);

    // state
    function onStateChange(nextKey) {
      if (nextKey === lastKey) {
        if (minOne) return;

        Toggle.toggle(toggleObj, null);
        lastKey = null;
        return;
      }

      Toggle.toggle(toggleObj, nextKey === key);
      lastKey = nextKey;
    }
    state.subscribe(onStateChange);

    function destroy() {
      state.unsubscribe(onStateChange);
    }

    return {
      destroy,
    };
  }

  static destroySingle({ entityObj }) {
    entityObj?.destroy();
  }

  static globalUpdate() {
    window.dispatchEvent(new Event("scroll")); // to update intersection observers
  }

  static toggle(toggleObj, isActive) {
    if (isActive) {
      Toggle.globalUpdate();
      Toggle.show(toggleObj, isActive);
    } else {
      Toggle.hide(toggleObj, isActive).then(() => {
        Toggle.globalUpdate();
      });
    }
  }

  static show(toggleObj) {
    Toggle.loadVideos(toggleObj);

    switch (toggleObj.mode) {
      case "height-opacity":
        return Toggle.heightOpacityTransition(toggleObj, true);

      case "height":
        return Toggle.heightTransition(toggleObj, true);

      default:
        return Toggle.basicNoTransition(toggleObj, true);
    }
  }

  static hide(toggleObj) {
    Toggle.stopVideos(toggleObj);

    switch (toggleObj.mode) {
      case "height-opacity":
        return Toggle.heightOpacityTransition(toggleObj, false);

      case "height":
        return Toggle.heightTransition(toggleObj, false);

      default:
        return Toggle.basicNoTransition(toggleObj, false);
    }
  }

  static loadVideos(toggleObj) {
    const placeholders = toggleObj.el.querySelectorAll("[data-yt-lazy]");
    placeholders.forEach((placeholder) => {
      const videoId = placeholder.dataset.videoId;
      if (!videoId) return;

      const iframe = document.createElement("iframe");
      iframe.src = `https://www.youtube.com/embed/${videoId}`;
      iframe.title = "YouTube video player";
      iframe.className = "absolute top-0 left-0 w-full h-full";
      iframe.frameBorder = "0";
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.allowFullscreen = true;
      iframe.setAttribute("data-yt-iframe", "");
      iframe.dataset.videoId = videoId;

      placeholder.innerHTML = "";
      placeholder.removeAttribute("data-yt-lazy");
      placeholder.appendChild(iframe);
    });
  }

  static stopVideos(toggleObj) {
    const iframes = toggleObj.el.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      const wrapper = iframe.parentElement;
      const videoId = iframe.dataset.videoId;

      wrapper.innerHTML = `
        <img 
            src="https://img.youtube.com/vi/${videoId}/maxresdefault.jpg" 
            alt="Miniatura filmu" 
            class="absolute top-0 left-0 w-full h-full object-cover"
            loading="lazy"
        />
      `;
      wrapper.setAttribute("data-yt-lazy", "");
      wrapper.dataset.videoId = videoId;
    });
  }

  static setActive(toggleObj, isActive) {
    if (toggleObj.activeClass) setClassName(toggleObj.el, toggleObj.activeClass, isActive);
    setAttr(toggleObj.el, toggleObj.activeAttr, isActive ? "" : null);
  }

  static setTransition(toggleObj, isActive) {
    setAttr(toggleObj.el, toggleObj.transitionAttr, isActive ? "" : null);
  }

  static basicNoTransition(toggleObj, isActive) {
    Toggle.setActive(toggleObj, isActive);
    return Promise.resolve();
  }

  static heightOpacityTransition(toggleObj, isActive) {
    const transitionProps = isActive
      ? {
          // enter
          height: ["0px", `${toggleObj.el.scrollHeight}px`],
          opacity: [0, 1],
        }
      : {
          // leave
          height: [`${toggleObj.el.offsetHeight}px`, "0px"],
          opacity: [1, 0],
        };

    return Toggle.transition(toggleObj, isActive, transitionProps);
  }

  static heightTransition(toggleObj, isActive) {
    const transitionProps = isActive
      ? {
          // enter
          height: ["0px", `${toggleObj.el.scrollHeight}px`],
        }
      : {
          // leave
          height: [`${toggleObj.el.offsetHeight}px`, "0px"],
        };

    return Toggle.transition(toggleObj, isActive, transitionProps);
  }

  static transition(toggleObj, isActive, transitionProps) {
    return new Promise((resolve) => {
      transition(toggleObj.el, transitionProps, {
        duration: 400,
        easing: "cubic-bezier(0.16, 1, 0.3, 1)",
        onStart: () => {
          if (isActive) Toggle.setActive(toggleObj, true);
          Toggle.setTransition(toggleObj, true);
        },
        onEnd: () => {
          if (!isActive) Toggle.setActive(toggleObj, false);
          Toggle.setTransition(toggleObj, false);
          resolve(true);
        },
        onCancel: () => {
          Toggle.setActive(toggleObj, isActive);
          Toggle.setTransition(toggleObj, false);
          resolve(false);
        },
      });
    });
  }
}
